import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import { normalizeTemplatePayload } from '../api/transform';
import { DocumentTemplate, DocumentTemplateStore } from '../types';
import { documentTemplateResourceSlice } from './resource-section';
import { documentTemplateSectionSlice } from './section';
import { documentTemplateTaskSlice } from './task';
import { variablesSlice } from './variables';

const INITIAL_STATE: DocumentTemplateStore = {};

export const documentTemplateSlice = createSlice({
  initialState: INITIAL_STATE,
  name: '@@TEMPLATES/DOCUMENT',
  reducers: {
    fetchDocumentTemplateStart: (
      state: DocumentTemplateStore,
      { payload }: PayloadAction<AtLeast<DocumentTemplate, 'templateId'>>,
    ): DocumentTemplateStore => {
      return dotPropImmutable.set(state, `${payload.templateId}.loading`, true);
    },

    fetchDocumentTemplateStartSuccess: (
      state: DocumentTemplateStore,
      { payload }: PayloadAction<DocumentTemplate>,
    ): DocumentTemplateStore => {
      const { entities } = normalizeTemplatePayload(payload);

      const payloadToMerge = {
        loading: false,
        error: null,
        sections: entities.sections || {},
        resourceSections: entities.resourceSections || {},
        template: entities.template[payload.templateId],
      };

      return dotPropImmutable.merge(state, payload.templateId, payloadToMerge);
    },

    updateDocumentTemplate: (
      state: DocumentTemplateStore,
      { payload }: PayloadAction<AtLeast<DocumentTemplate, 'templateId'>>,
    ): DocumentTemplateStore => {
      return dotPropImmutable.set(
        state,
        `${payload.templateId}.template`,
        payload,
      );
    },

    ...documentTemplateSectionSlice,
    ...documentTemplateResourceSlice,
    ...documentTemplateTaskSlice,
    ...variablesSlice,
  },
});

export const {
  fetchDocumentTemplateStart,
  fetchDocumentTemplateStartSuccess,
  updateDocumentTemplate,
  createDocumentTemplateSection,
  updateDocumentTemplateContent,
  updateDocumentTemplateTask,
  deleteDocumentTemplateTask,
  createDocumentResourceSection,
  updateDocumentResourceSection,
  createDocumentTemplateHighlightTask,
  setConditionalVariables: setConditionalVariablesInDocumentTemplate,
} = documentTemplateSlice.actions;
